import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import { motion } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: ${({ $scrolled }) => ($scrolled ? '10px 0' : '15px 0')};
  background-color: ${({ $scrolled }) => ($scrolled ? 'var(--navbar-bg)' : 'transparent')};
  transition: all 0.3s ease-in-out;
  box-shadow: ${({ $scrolled }) => ($scrolled ? '0 4px 20px rgba(0, 0, 0, 0.15)' : 'none')};
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? 'blur(10px)' : 'none')};
  
  @media (max-width: 480px) {
    padding: ${({ $scrolled }) => ($scrolled ? '8px 0' : '12px 0')};
  }
`;

const NavWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 480px) {
    width: 95%;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
  }
  
  img {
    height: 100px;
    filter: ${({ $scrolled }) => ($scrolled ? 'brightness(0) invert(1) drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2))' : 'brightness(0) invert(1) drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2))')};
    
    @media (max-width: 992px) {
      height: 80px;
    }
    
    @media (max-width: 768px) {
      height: 70px;
    }
    
    @media (max-width: 480px) {
      height: 60px;
    }
  }
`;

const MenuToggle = styled.div`
  display: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: white;
  
  @media (max-width: 992px) {
    display: block;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 992px) {
    position: fixed;
    top: 0;
    right: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
    width: 280px;
    height: 100vh;
    background-color: var(--dark-bg);
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 0;
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
    padding: 80px 40px 40px;
    transition: right 0.3s ease-in-out;
    z-index: 1001;
  }
  
  @media (max-width: 480px) {
    width: 250px;
    padding: 70px 30px 30px;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLinkStyled = styled(Link)`
  position: relative;
  font-size: 1rem;
  font-weight: ${({ $active }) => ($active ? '600' : '500')};
  color: ${({ $scrolled, $active }) => 
    $scrolled
      ? 'white'
      : 'var(--text-light)'};
  transition: all 0.3s ease;
  padding: 10px 5px;
  cursor: pointer;
  
  &:hover {
    color: ${({ $scrolled }) => 
    $scrolled
      ? 'white'
      : 'white'};
  }
  
  @media (max-width: 992px) {
    font-size: 1.1rem;
    width: 100%;
    padding: 15px 0;
    color: white;
    
    &:hover {
      color: var(--secondary-color);
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 12px 0;
  }
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  bottom: -4px;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--secondary-color), var(--secondary-light));
  border-radius: 4px;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  cursor: pointer;
  display: none;
  color: white;
  
  @media (max-width: 992px) {
    display: block;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    top: 0.8rem;
    right: 0.8rem;
  }
`;

const navLinks = [
  { id: 'home', text: 'Home' },
  { id: 'services', text: 'Services' },
  { id: 'about', text: 'About Us' },
  { id: 'blog', text: 'Blog' },
  { id: 'contact', text: 'Contact' }
];

const Navbar = ({ scrolled }) => {
  const [activeLink, setActiveLink] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSetActive = (to) => {
    setActiveLink(to);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <NavContainer $scrolled={scrolled}>
      <NavWrapper>
        <Logo $scrolled={scrolled}>
          <img src={`${process.env.PUBLIC_URL}/Websigma_logo.png`} alt="Websigma Logo" />
        </Logo>
        
        <MenuToggle onClick={toggleMenu}>
          <FaBars />
        </MenuToggle>
        
        <NavMenu $isOpen={isMenuOpen}>
          <CloseButton onClick={closeMenu}>
            <FaTimes />
          </CloseButton>
          
          {navLinks.map((link) => (
            <NavItem key={link.id}>
              <NavLinkStyled
                to={link.id}
                spy={true}
                smooth={true}
                offset={-80}
                duration={500}
                onSetActive={handleSetActive}
                $active={activeLink === link.id}
                $scrolled={scrolled}
                onClick={closeMenu}
              >
                {link.text}
                {activeLink === link.id && (
                  <ActiveIndicator
                    layoutId="activeIndicator"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </NavLinkStyled>
            </NavItem>
          ))}
        </NavMenu>
      </NavWrapper>
    </NavContainer>
  );
};

export default Navbar;
